import { router, usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import type { SharedProps } from '../types'
import type { AppNotification } from '../types/notification'
import {
  ensureNotificationStream,
  loadNotifications,
  markAllRead,
  markRead,
  seedUnread,
  useNotifications,
} from './notificationStore'
import { ensurePresenceStream } from './VirtualOffice/presenceStream'

// 今天的只显示时分，更早的带日期 —— 这一行要塞得下人名和任务名，能省的字都省掉。
// 与 Todo/CommentList 的 stamp 同一个口径，但那份是组件内的局部函数，
// 抽成共用模块只为两处共用一个 12 行的格式化函数不划算。
function stamp(iso: string): string {
  const date = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`
  const today = new Date()
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()

  return sameDay ? clock : `${date.getMonth() + 1}月${date.getDate()}日 ${clock}`
}

// 顶栏那个铃铛（PRD 2026-10-31-mentions §7）。
//
// 未读数走 ActionCable 实时推（NotificationsChannel），页面不用刷新；整页加载
// 时先由共享 props 播种，免得连上之前红点是空的。列表仍然点开才取 —— 大多数
// 页面加载里没有人会点开它。
//
// 状态在 notificationStore 里而不是这里：Header 同时挂着两个这个组件
// （宽屏一个、窄屏一个），状态留在组件内就是两份状态、两条 WebSocket。
const NotificationBell: React.FC = () => {
  const { props } = usePage<SharedProps>()
  const { unread, rows, loading } = useNotifications()
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  // 服务端真值：整页加载和每次 Inertia 导航都会带来新的一份。
  useEffect(() => seedUnread(props.unread_notifications ?? 0), [props.unread_notifications])

  // 登录之后才连。两个铃铛都调，ensure 自己幂等。
  useEffect(() => ensureNotificationStream(), [])
  // Presence for /virtual_office rides on the same signed-in-only mount.
  useEffect(() => ensurePresenceStream(), [])

  useEffect(() => {
    if (!open) return undefined

    const onClickAway = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onClickAway)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickAway)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open])

  const toggle = () => {
    const next = !open
    setOpen(next)
    // 每次点开都重取：cable 断过线的那段时间里来的几条只有重取才补得上，
    // 而推送只保证「连着的时候」。
    if (next) void loadNotifications()
  }

  const openOne = (row: AppNotification) => {
    setOpen(false)
    if (!row.read) void markRead(row.id)
    router.visit(`/todo/${row.task.url_key}`)
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `通知（${unread} 条未读）` : '通知'}
        className="relative flex h-8 w-8 items-center justify-center rounded-full text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink"
      >
        {/* 内联 SVG：顶栏其余图标也是内联的，为一个铃铛引图标库不值得。 */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3a6 6 0 0 0-6 6v3.6l-1.4 2.8A.8.8 0 0 0 5.3 17h13.4a.8.8 0 0 0 .7-1.6L18 12.6V9a6 6 0 0 0-6-6Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M9.5 20a2.5 2.5 0 0 0 5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-brand-primary px-1 text-center text-[10px] font-semibold leading-4 text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-brand-line bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-brand-line px-3 py-2">
            <span className="flex-1 text-sm font-medium text-brand-ink">通知</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-brand-ink2 hover:text-brand-ink"
              >
                全部已读
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {/* rows 还没取过时才显示「取通知中」。重取时留着旧列表 ——
                cable 推来一条之后点开，列表当场清空再填回来是闪一下。 */}
            {loading && rows === null && <div className="px-3 py-4 text-sm text-brand-ink2">取通知中…</div>}

            {rows?.length === 0 && (
              // 空态说清楚它什么时候会响 —— 否则一个永远空着的铃铛只会让人
              // 以为它坏了。
              <div className="px-3 py-4 text-sm text-brand-ink2">
                还没有通知。别人在共享项目的任务留言里 @ 你时，这里会亮。
              </div>
            )}

            {(rows ?? []).map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => openOne(row)}
                className={`block w-full border-b border-brand-line px-3 py-2 text-left last:border-b-0 hover:bg-brand-glass2 ${
                  row.read ? '' : 'bg-brand-primarySoft'
                }`}
              >
                <div className="text-[13px] text-brand-ink">
                  <strong className="font-medium">{row.actor}</strong> 在「{row.task.title}」里提到了你
                </div>
                {row.excerpt && (
                  <div className="mt-0.5 line-clamp-2 text-[12px] text-brand-ink2">{row.excerpt}</div>
                )}
                <div className="mt-0.5 text-[11px] text-brand-ink2">
                  {row.task.project && <span>{row.task.project} · </span>}
                  {stamp(row.created_at)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
